<template>
  <NavBarSettings>
    <!-- Back to the workspace dashboard the user came from (TRV-1206); falls
         back to the workspace chooser ("All workspaces") when there's no recent
         workspace to return to (e.g. opened straight from the chooser). -->
    <template #top>
      <NuxtLink
        v-tooltip.right="navCollapsed ? backLabel : undefined"
        class="group flex w-full items-center gap-1.5 rounded-lg p-2 text-surface-700 transition-colors hover:bg-surface-100 hover:text-surface-900"
        :class="navCollapsed ? 'justify-center' : ''"
        :to="backTo"
        data-testid="settings-back-link"
      >
        <AppIcon
          icon="fa-solid fa-chevron-left"
          class="text-sm text-surface-500"
        />
        <span
          v-if="!navCollapsed"
          class="truncate text-sm font-medium"
          >{{ backLabel }}</span
        >
      </NuxtLink>
    </template>

    <!-- Section navigation — one route per section (studio-design tv-sidebar
         nav: 6px gap between items). -->
    <template #nav-items>
      <div class="flex flex-col gap-1.5">
        <NavBarSettingsItem
          v-for="item in sections"
          :key="item.name"
          :label="item.label"
          :icon="item.icon"
          :to="{ name: item.name }"
          :testid="`settings-nav-${item.name}`"
        />
      </div>
    </template>

    <template #footer>
      <NavBarAccountButton />
    </template>
  </NavBarSettings>
</template>

<script setup lang="ts">
import { getFeatureActive } from '~/utils/getFeatureActive'
import { useLayoutPreferencesStore } from '~/stores/layoutPreferences'

// This nav is always the flat variant, so it collapses to a rail whenever the
// layout is minimized. Computed locally rather than injected because NavBar (the
// provider) is a child of this component.
const layoutStore = useLayoutPreferencesStore()
const navCollapsed = computed(() => layoutStore.currentLayout === 'float')

const { backTo, backLabel } = useSettingsBackDestination()

// Voices only when the VoiceCall feature flag is on (its route is likewise
// flag-guarded).
const sections = [
  {
    name: 'system-admin-summary',
    label: 'Summary',
    icon: 'fa-regular fa-chart-tree-map',
  },
  {
    name: 'system-admin-organisations',
    label: 'Organisations',
    icon: 'fa-regular fa-building',
  },
  {
    name: 'system-admin-workspaces',
    label: 'Workspaces',
    icon: 'fa-regular fa-grid',
  },
  {
    name: 'system-admin-users',
    label: 'Users',
    icon: 'fa-regular fa-user-group',
  },
  {
    name: 'system-admin-simulations',
    label: 'Simulations',
    icon: 'fa-regular fa-film',
  },
  {
    name: 'system-admin-characters',
    label: 'Characters',
    icon: 'fa-regular fa-person',
  },
  ...(getFeatureActive('VoiceCall')
    ? [
        {
          name: 'system-admin-voices',
          label: 'Voices',
          icon: 'fa-regular fa-microphone',
        },
      ]
    : []),
]
</script>
